import { useServices } from "@hq/client-react";
import type { Task } from "@hq/contracts";
import { addDays, calendarDateOf, todayOf, type NowContext } from "@hq/core";
import { Button, Calendar, Popover, toast } from "@z/ui";
import { useState, type ReactElement } from "react";

/** A work day is distinct from a deadline and does not require inventing a time. */
export function WhenPopover({
  task,
  now,
  trigger,
}: {
  task: Task;
  now: NowContext;
  trigger: ReactElement;
}) {
  const services = useServices();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const today = todayOf(now);
  const date =
    task.plannedDate ??
    (task.scheduledAt ? calendarDateOf(task.scheduledAt, now.timeZone) : undefined);
  const choose = async (workDay: string | null, evening = false, someday = false) => {
    setSaving(true);
    try {
      if (someday) await services.tasks.setStatus(task.id, "someday");
      else {
        await services.tasks.plan(task.id, workDay);
        if (evening) await services.tasks.setEvening(task.id, true);
      }
      setOpen(false);
    } catch {
      toast({ title: "Could not change when", variant: "danger" });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger render={trigger} />
      <Popover.Content align="start" className="w-auto p-0" aria-label="When">
        <div className="px-3 pt-3">
          <Popover.Title>When will you work on this?</Popover.Title>
          <p className="mt-1 text-xs text-fg-muted">Your deadline stays unchanged.</p>
        </div>
        <div className="grid grid-cols-2 gap-1 p-2" inert={saving}>
          <Button variant="ghost" size="sm" onClick={() => void choose(today)}>
            Today
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void choose(today, true)}>
            This evening
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void choose(addDays(today, 1))}>
            Tomorrow
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void choose(null)}>
            Anytime
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void choose(null, false, true)}>
            Someday
          </Button>
        </div>
        <div inert={saving}>
          <Calendar
            today={today}
            {...(date ? { value: date } : {})}
            onChange={(day) => void choose(day)}
          />
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
